import * as d3 from "d3"
import colormap from "colormap"
import { BooleanTileGrid, CategoricalTileGrid, NumericTileGrid, type TileGridProps } from "./tile_grid"
import { TableDataset } from "./table_dataset"
import { getMedianCellSize } from "./components/cell_area_component"

export interface DatasetNumericStats {
  sum: number
  min: number
  max: number
  range: number
  mean: number
  mode: number
  step: number
  median: number
  stdDevSum: number
  stdDevMean: number
}

export const DATASET_CHART_KEY = "datasetChart"
export const TABLE_CHART_KEY = "tableChart"
export const TABLE_COMPARE_CHART_KEY = "tableCompareChart"
export const SPIDER_CHART_KEY = "spiderChart"
export const DEFAULT_HISTOGRAM_BINS = 10
export const MAX_HISTOGRAM_BINS = 40
export const HISTOGRAM_FILL = "jet"

export type DatasetChartKind = "pie" | "distribution" | "hist"
export type DatasetChartSourceType = "BooleanTileGrid" | "CategoricalTileGrid" | "NumericTileGrid"

type Rgba = [number, number, number, number]
type ChartGrid = BooleanTileGrid | CategoricalTileGrid | NumericTileGrid

export interface SerializedDatasetChart {
  sourceType: DatasetChartSourceType
  count: Array<[string | number, number]>
  colors: Array<[string | number, Rgba]>
  numericStats?: DatasetNumericStats
  fullNumericStats?: DatasetNumericStats
  inputHistogramBins: number
  inputFillType: string
  cellArea: number
  props?: TileGridProps
}

const NO_DATA_LABEL = "No Data"
const NO_DATA_COLOR: Rgba = [158, 158, 158, 1]
const BOOLEAN_FALSE_COLOR: Rgba = [189, 189, 189, 1]
const BOOLEAN_TRUE_COLOR: Rgba = [76, 120, 168, 1]

export interface SerializedTableChartRow {
  label: string
  mean: number
}

export interface SerializedTableChart {
  tableName: string
  rows: SerializedTableChartRow[]
}

export function tableChartFromInputs(grid: CategoricalTileGrid, table: TableDataset, mask?: BooleanTileGrid): SerializedTableChart {
  const counts = new Map<string, number>()
  for (const [label, area] of datasetToChartData(grid, { mask }).count) {
    if (typeof label === "string") counts.set(label, area)
  }

  const rows: SerializedTableChartRow[] = []
  for (let rowIndex = 0; rowIndex < table.rows.length; rowIndex++) {
    let weightedSum = 0
    let totalWeight = 0

    counts.forEach((area, label) => {
      if (label.trim().toLowerCase() === "no data") return
      if (!Number.isFinite(area) || area <= 0) return

      const columnIndex = table.columns.findIndex(
        column => column.trim().toLowerCase() === label.trim().toLowerCase()
      )
      if (columnIndex < 0) return

      const cellValue = table.data[rowIndex]?.[columnIndex]
      if (typeof cellValue !== "number" || !Number.isFinite(cellValue)) return

      weightedSum += cellValue * area
      totalWeight += area
    })

    if (totalWeight <= 0) continue
    rows.push({
      label: table.rows[rowIndex]?.trim() || `Row ${rowIndex + 1}`,
      mean: weightedSum / totalWeight,
    })
  }

  return {
    tableName: table.name?.trim() || "Table",
    rows,
  }
}

export function readTableChart(value: unknown): SerializedTableChart | null {
  if (!value || typeof value !== "object") return null
  const rec = value as Partial<SerializedTableChart>
  if (!Array.isArray(rec.rows)) return null
  return {
    tableName: typeof rec.tableName === "string" && rec.tableName.trim() ? rec.tableName : "Table",
    rows: readTableChartRows(rec.rows),
  }
}

export interface SerializedTableCompareChart {
  tableName: string
  nameA: string
  nameB: string
  rowsA: SerializedTableChartRow[]
  rowsB: SerializedTableChartRow[]
}

export function tableCompareChartFromInputs(
  gridA: CategoricalTileGrid,
  gridB: CategoricalTileGrid,
  table: TableDataset,
  mask?: BooleanTileGrid
): SerializedTableCompareChart {
  return {
    tableName: table.name?.trim() || "Table",
    nameA: gridA.name?.trim() || "A",
    nameB: gridB.name?.trim() || "B",
    rowsA: tableChartFromInputs(gridA, table, mask).rows,
    rowsB: tableChartFromInputs(gridB, table, mask).rows,
  }
}

/** Area-weighted means for the transition from → to. Swapped flips B → A. */
export function tableCompareDifference(
  chart: SerializedTableCompareChart,
  swapped: boolean
): SerializedTableChartRow[] {
  const fromRows = swapped ? chart.rowsB : chart.rowsA
  const toRows = swapped ? chart.rowsA : chart.rowsB
  const toByLabel = new Map<string, number>()
  for (const row of toRows) toByLabel.set(row.label.trim().toLowerCase(), row.mean)

  const rows: SerializedTableChartRow[] = []
  for (const row of fromRows) {
    const other = toByLabel.get(row.label.trim().toLowerCase())
    if (other === undefined) continue
    rows.push({ label: row.label, mean: row.mean - other })
  }
  return rows
}

export function tableCompareEndpointNames(
  chart: SerializedTableCompareChart,
  swapped: boolean
): { from: string; to: string } {
  return swapped
    ? { from: chart.nameB, to: chart.nameA }
    : { from: chart.nameA, to: chart.nameB }
}

export function readTableCompareChart(value: unknown): SerializedTableCompareChart | null {
  if (!value || typeof value !== "object") return null
  const rec = value as Partial<SerializedTableCompareChart>
  return {
    tableName: typeof rec.tableName === "string" && rec.tableName.trim() ? rec.tableName : "Table",
    nameA: typeof rec.nameA === "string" && rec.nameA.trim() ? rec.nameA : "A",
    nameB: typeof rec.nameB === "string" && rec.nameB.trim() ? rec.nameB : "B",
    rowsA: readTableChartRows(rec.rowsA),
    rowsB: readTableChartRows(rec.rowsB),
  }
}

export interface SerializedSpiderSeries {
  name: string
  rows: SerializedTableChartRow[]
}

export interface SerializedSpiderChart {
  tableName: string
  /** Spoke labels in table-row order. */
  axes: string[]
  series: SerializedSpiderSeries[]
}

export function spiderChartFromInputs(grids: CategoricalTileGrid[], table: TableDataset, mask?: BooleanTileGrid): SerializedSpiderChart {
  const usedNames: string[] = []
  const series: SerializedSpiderSeries[] = grids.map((grid, index) => {
    const name = uniqueSeriesName(usedNames, grid.name?.trim() || `Dataset ${index + 1}`)
    usedNames.push(name)
    return { name, rows: tableChartFromInputs(grid, table, mask).rows }
  })

  const present = new Set<string>()
  for (const entry of series) {
    for (const row of entry.rows) present.add(row.label.trim().toLowerCase())
  }

  const axes: string[] = []
  const seen = new Set<string>()
  for (let index = 0; index < table.rows.length; index++) {
    const label = table.rows[index]?.trim() || `Row ${index + 1}`
    const key = label.toLowerCase()
    if (seen.has(key) || !present.has(key)) continue
    seen.add(key)
    axes.push(label)
  }

  return {
    tableName: table.name?.trim() || "Table",
    axes,
    series,
  }
}

export function spiderChartHasData(chart: SerializedSpiderChart | null): boolean {
  if (!chart) return false
  return chart.axes.length > 0 && chart.series.some(entry => entry.rows.length > 0)
}

export function readSpiderChart(value: unknown): SerializedSpiderChart | null {
  if (!value || typeof value !== "object") return null
  const rec = value as Partial<SerializedSpiderChart>
  const series: SerializedSpiderSeries[] = []
  if (Array.isArray(rec.series)) {
    for (const entry of rec.series) {
      if (!entry || typeof entry !== "object") continue
      const name = typeof entry.name === "string" ? entry.name.trim() : ""
      if (!name) continue
      series.push({ name, rows: readTableChartRows(entry.rows) })
    }
  }
  const axes: string[] = []
  if (Array.isArray(rec.axes)) {
    for (const axis of rec.axes) {
      if (typeof axis === "string" && axis.trim()) axes.push(axis)
    }
  }
  return {
    tableName: typeof rec.tableName === "string" && rec.tableName.trim() ? rec.tableName : "Table",
    axes,
    series,
  }
}

function uniqueSeriesName(used: string[], base: string): string {
  const trimmed = base.trim() || "Dataset"
  const taken = (name: string) => used.some(existing => existing.toLowerCase() === name.toLowerCase())
  if (!taken(trimmed)) return trimmed
  let count = 2
  while (taken(`${trimmed} (${count})`)) count += 1
  return `${trimmed} (${count})`
}

function readTableChartRows(value: unknown): SerializedTableChartRow[] {
  if (!Array.isArray(value)) return []
  const rows: SerializedTableChartRow[] = []
  for (const row of value) {
    if (!row || typeof row !== "object") continue
    const label = typeof row.label === "string" ? row.label : ""
    const mean = Number(row.mean)
    if (!label || !Number.isFinite(mean)) continue
    rows.push({ label, mean })
  }
  return rows
}

export function readHistogramBins(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value)
  if (!Number.isFinite(n)) return DEFAULT_HISTOGRAM_BINS
  return Math.max(1, Math.min(MAX_HISTOGRAM_BINS, Math.round(n)))
}

export function datasetToChartData(
  grid: ChartGrid,
  options?: { bins?: number; fillType?: string; mask?: BooleanTileGrid }
): SerializedDatasetChart {
  const cellArea = getMedianCellSize(grid).area
  const cellKm2 = cellArea / 1_000_000
  const bins = readHistogramBins(options?.bins)
  const fillType = options?.fillType || HISTOGRAM_FILL
  const mask = options?.mask

  if (grid instanceof NumericTileGrid) {
    return numericChart(grid, cellArea, cellKm2, bins, fillType, mask)
  }
  if (grid instanceof BooleanTileGrid) {
    return booleanChart(grid, cellArea, cellKm2, bins, fillType, mask)
  }
  return categoricalChart(grid, cellArea, cellKm2, bins, fillType, mask)
}

function outsideMask(mask: BooleanTileGrid | undefined, x: number, y: number, zoom: number): boolean {
  return mask != null && mask.get(x, y, zoom) !== true
}

export function readDatasetChart(value: unknown): SerializedDatasetChart | null {
  if (!value || typeof value !== "object") return null
  const rec = value as Partial<SerializedDatasetChart>
  if (rec.sourceType !== "BooleanTileGrid" && rec.sourceType !== "CategoricalTileGrid" && rec.sourceType !== "NumericTileGrid") {
    return null
  }
  if (!Array.isArray(rec.count) || !Array.isArray(rec.colors)) return null

  const count: Array<[string | number, number]> = []
  for (const pair of rec.count) count.push(...readCountPair(pair))
  const colors: Array<[string | number, Rgba]> = []
  for (const pair of rec.colors) colors.push(...readColorPair(pair))
  if (count.length === 0 && colors.length === 0 && rec.count.length > 0) return null

  return {
    sourceType: rec.sourceType,
    count,
    colors,
    numericStats: readNumericStats(rec.numericStats),
    fullNumericStats: readNumericStats(rec.fullNumericStats),
    inputHistogramBins: readHistogramBins(rec.inputHistogramBins),
    inputFillType: typeof rec.inputFillType === "string" ? rec.inputFillType : "",
    cellArea: typeof rec.cellArea === "number" && Number.isFinite(rec.cellArea) ? rec.cellArea : 0,
    props: readProps(rec.props),
  }
}

function booleanChart(
  grid: BooleanTileGrid,
  cellArea: number,
  cellKm2: number,
  bins: number,
  fillType: string,
  mask?: BooleanTileGrid
): SerializedDatasetChart {
  let falses = 0
  let trues = 0
  grid.iterate((x, y, value) => {
    if (outsideMask(mask, x, y, grid.zoom)) return
    if (value) trues++
    else falses++
  })

  const count: Array<[string, number]> = []
  const colors: Array<[string, Rgba]> = []
  if (falses > 0) {
    count.push(["false", falses * cellKm2])
    colors.push(["false", BOOLEAN_FALSE_COLOR])
  }
  if (trues > 0) {
    count.push(["true", trues * cellKm2])
    colors.push(["true", BOOLEAN_TRUE_COLOR])
  }

  return {
    sourceType: "BooleanTileGrid",
    count,
    colors,
    inputHistogramBins: bins,
    inputFillType: fillType,
    cellArea,
  }
}

function categoricalChart(
  grid: CategoricalTileGrid,
  cellArea: number,
  cellKm2: number,
  bins: number,
  fillType: string,
  mask?: BooleanTileGrid
): SerializedDatasetChart {
  const labels = grid.labels instanceof Map ? grid.labels : new Map<number, string>()
  const totals = new Map<string, number>()
  const colorByLabel = new Map<string, Rgba>()
  const order: string[] = []

  const remember = (label: string, key: number) => {
    if (totals.has(label)) return
    order.push(label)
    totals.set(label, 0)
    colorByLabel.set(label, label === NO_DATA_LABEL && key === 255 ? NO_DATA_COLOR : paletteColor(key))
  }

  const keys = Array.from(labels.keys()).filter(key => key !== 255).sort((a, b) => a - b)
  for (const key of keys) {
    remember(labels.get(key) || NO_DATA_LABEL, key)
  }

  grid.iterate((x, y, key) => {
    if (outsideMask(mask, x, y, grid.zoom)) return
    const label = labels.get(key) || NO_DATA_LABEL
    remember(label, key)
    totals.set(label, (totals.get(label) || 0) + 1)
  })

  const count: Array<[string, number]> = []
  const colors: Array<[string, Rgba]> = []
  for (const label of order) {
    const cells = totals.get(label) || 0
    if (cells <= 0) continue
    count.push([label, cells * cellKm2])
    colors.push([label, colorByLabel.get(label) || NO_DATA_COLOR])
  }

  return {
    sourceType: "CategoricalTileGrid",
    count,
    colors,
    inputHistogramBins: bins,
    inputFillType: fillType,
    cellArea,
  }
}

function numericChart(
  grid: NumericTileGrid,
  cellArea: number,
  cellKm2: number,
  bins: number,
  fillType: string,
  mask?: BooleanTileGrid
): SerializedDatasetChart {
  const af = unitsAdjustmentFactor(grid.properties.area, grid)
  const valueCounts = new Map<number, number>()
  let stdDevSum = 0
  let n = 0

  grid.iterate((x, y, value) => {
    if (outsideMask(mask, x, y, grid.zoom)) return
    if (!Number.isFinite(value)) return
    valueCounts.set(value, (valueCounts.get(value) || 0) + 1)
    stdDevSum += (value * af) ** 2
    n++
  })

  const base = {
    sourceType: "NumericTileGrid" as const,
    inputHistogramBins: bins,
    inputFillType: fillType,
    cellArea,
    props: grid.properties,
  }

  if (valueCounts.size === 0 || n === 0) {
    return { ...base, count: [], colors: [] }
  }

  const mapEntries = Array.from(valueCounts.entries()).sort((a, b) => a[0] - b[0])
  const calculatedMin = mapEntries[0][0]
  const calculatedMax = mapEntries[mapEntries.length - 1][0]
  const calculatedRange = calculatedMax - calculatedMin
  const calculatedStep = calculatedRange / bins
  let fullDataSum = 0
  for (const [value, count] of mapEntries) fullDataSum += value * count
  const fullDataMean = fullDataSum / n
  const fullDataMedian = medianFromMap(mapEntries, n) || 0
  const fullDataMode = mapEntries.reduce((max, current) => current[1] > max[1] ? current : max, mapEntries[0])[0]
  const stdDev = Math.sqrt(stdDevSum)
  const stats: DatasetNumericStats = {
    sum: fullDataSum * af,
    min: calculatedMin,
    max: calculatedMax,
    median: fullDataMedian,
    range: calculatedRange,
    mean: fullDataMean,
    mode: fullDataMode,
    step: calculatedStep,
    stdDevSum: stdDev,
    stdDevMean: stdDev / Math.sqrt(n),
  }

  const [dsMin, dsMax] = grid.getStats().min !== undefined
    ? [grid.getStats().min, grid.getStats().max]
    : [calculatedMin, calculatedMax]
  const fillMap = getColorStops(fillType === "greyscale" ? "greys" : (fillType === "heatmap" ? "jet" : fillType), 40).reverse()
  const counts = new Map<number, number>()
  const colors = new Map<number, Rgba>()

  for (let i = 0; i < bins; i++) {
    const lower = calculatedMin + i * calculatedStep
    counts.set(lower, 0)
    const mid = lower + calculatedStep / 2
    const span = dsMax - dsMin
    const t = span === 0 ? 0.5 : (mid - dsMin) / span
    colors.set(lower, colorForBin(t, fillMap))
  }

  for (const [value, count] of mapEntries) {
    addValueToBins(counts, value, count, calculatedMin, calculatedMax, calculatedStep, bins)
  }

  const countPairs: Array<[number, number]> = []
  const colorPairs: Array<[number, Rgba]> = []
  counts.forEach((cells, lower) => {
    countPairs.push([lower, cells * cellKm2])
    colorPairs.push([lower, colors.get(lower) || [102, 102, 102, 1]])
  })

  return {
    ...base,
    count: countPairs,
    colors: colorPairs,
    numericStats: stats,
    fullNumericStats: stats,
  }
}

function addValueToBins(
  counts: Map<number, number>,
  value: number,
  count: number,
  min: number,
  max: number,
  step: number,
  bins: number
) {
  if (step === 0 || bins <= 1) {
    const lower = min
    counts.set(lower, (counts.get(lower) || 0) + count)
    return
  }

  let index = Math.floor((value - min) / step)
  if (value >= max) index = bins - 1
  if (index < 0) index = 0
  if (index >= bins) index = bins - 1

  const lower = min + index * step
  counts.set(lower, (counts.get(lower) || 0) + count)
  if (index > 0 && value === lower) {
    const previous = min + (index - 1) * step
    counts.set(previous, (counts.get(previous) || 0) + count)
  }
}

function colorForBin(t: number, fillMap: any[]): Rgba {
  const stops = Math.floor(fillMap.length / 2)
  if (stops <= 0) return [102, 102, 102, 1]
  const safeT = Number.isFinite(t) ? Math.max(0, Math.min(1, t)) : 0
  let index = Math.floor(safeT * stops)
  index = Math.max(0, Math.min(stops - 1, index))
  const found = findColor(safeT, fillMap)
  const candidate = Array.isArray(found) ? found : fillMap[index * 2]
  return rgbaFrom(candidate)
}

function getColorStops(name: string, steps: number): any[] {
  const delta = (0 - 1) / (steps - 1)
  const stops: any[] = []
  let colors = colormap({ colormap: name === "BuRd" ? "RdBu" : name, nshades: steps, format: "rgba" })
  if (name !== "BuRd") colors = colors.slice().reverse()
  for (let i = 0; i < steps; i++) {
    stops.push(1 + i * delta)
    stops.push(colors[i])
  }
  return stops
}

function findColor(value: number, colorArray: any[]): unknown {
  let index = Math.floor(value * (colorArray.length / 2))
  index = Math.min(index, colorArray.length / 2 - 1)
  return colorArray[index * 2]
}

function paletteColor(key: number): Rgba {
  const palette = d3.schemeTableau10
  const index = ((Math.max(key, 1) - 1) % palette.length + palette.length) % palette.length
  return rgbaFrom(d3.color(palette[index])?.rgb())
}

function rgbaFrom(candidate: unknown): Rgba {
  if (Array.isArray(candidate) && candidate.length >= 3) {
    return [
      Number(candidate[0]),
      Number(candidate[1]),
      Number(candidate[2]),
      candidate.length >= 4 ? Number(candidate[3]) : 1,
    ]
  }
  if (candidate && typeof candidate === "object" && "r" in candidate && "g" in candidate && "b" in candidate) {
    const rgb = candidate as { r: number; g: number; b: number }
    return [rgb.r, rgb.g, rgb.b, 1]
  }
  return [102, 102, 102, 1]
}

function medianFromMap(arr: [number, number][], total: number): number | undefined {
  let idx = total / 2

  if (idx % 1 === 0) {
    for (let i = 0; i < arr.length; i++) {
      const [key, value] = arr[i]
      idx -= value
      if (idx <= 0) return key
    }
  } else {
    let lower = Math.floor(idx)
    let upper = Math.ceil(idx)
    for (let i = 0; i < arr.length; i++) {
      const [key, value] = arr[i]
      lower -= value
      upper -= value
      if (lower <= 0) {
        if (upper <= 0) return key
        const next = arr[i + 1]?.[0]
        return next === undefined ? key : (key + next) / 2
      }
    }
  }

  return undefined
}

function splitUnit(input: string): [number, string] {
  const trimmed = input.trim()
  const match = trimmed.match(/^(\d+(?:\.\d+)?)(.*)$/)
  if (match) {
    const [, num, unit] = match
    return [parseFloat(num), unit.trim()]
  }
  return [1, trimmed]
}

function unitsAdjustmentFactor(unit: string | undefined, grid: NumericTileGrid): number {
  if (!unit) return 1
  const area = getMedianCellSize(grid).area
  const [multiplier, unitStr] = splitUnit(unit)
  switch (unitStr) {
    case "m²":
      return area / (multiplier * 1)
    case "ha":
      return area / (multiplier * 10_000)
    case "km²":
      return area / (multiplier * 1_000_000)
    default:
      return multiplier
  }
}

function readCountPair(pair: unknown): Array<[string | number, number]> {
  if (!Array.isArray(pair) || pair.length < 2) return []
  const key = pair[0]
  const value = Number(pair[1])
  if (!Number.isFinite(value)) return []
  if (typeof key === "number" && Number.isFinite(key)) return [[key, value]]
  if (typeof key === "string") return [[key, value]]
  return []
}

function readColorPair(pair: unknown): Array<[string | number, Rgba]> {
  if (!Array.isArray(pair) || pair.length < 2) return []
  const key = pair[0]
  const color = rgbaFrom(pair[1])
  if (typeof key === "number" && Number.isFinite(key)) return [[key, color]]
  if (typeof key === "string") return [[key, color]]
  return []
}

function readNumericStats(value: unknown): DatasetNumericStats | undefined {
  if (!value || typeof value !== "object") return undefined
  const rec = value as Partial<DatasetNumericStats>
  const keys: Array<keyof DatasetNumericStats> = ["sum", "min", "max", "range", "mean", "mode", "step", "median", "stdDevSum", "stdDevMean"]
  const stats = {} as DatasetNumericStats
  for (const key of keys) {
    const n = Number(rec[key])
    if (!Number.isFinite(n)) return undefined
    stats[key] = n
  }
  return stats
}

function readProps(value: unknown): TileGridProps | undefined {
  if (!value || typeof value !== "object") return undefined
  const rec = value as Partial<TileGridProps>
  return {
    unit: typeof rec.unit === "string" ? rec.unit : undefined,
    area: typeof rec.area === "string" ? rec.area : undefined,
    type: typeof rec.type === "string" ? rec.type : undefined,
  }
}
